import React, { useContext, useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { AuthContext } from '../../context/auth-context';
import WorkflowAPI from '../../../services/api/workflow/workflow-api';
import StatusHistoryTable from './StatusHistoryTable';
import TimecardDetailTable from './TimecardDetailTable';
import EarnedHistoryTable from './EarnedHistoryTable';
import BudgetSplitTable from './BudgetSplitTable';
import BudgetChangeTable from './BudgetChangeTable';
import WeeklySnapshotsTable from './WeeklySnapshotsTable';
import './PackageHistoryModal.css';

/**
 * PackageHistoryModal — shared, read-only modal that shows six history/audit
 * tables for a work package. Opens stacked on top of the calling modal
 * (e.g. the Bryntum task editor) and lets the user switch between history
 * types via a pill switcher row. Each type lazy-loads its own data on first
 * view and caches it for the modal session; state resets when the modal closes.
 *
 * NOTE: Bryntum popups use z-index up to 99999, so this Dialog uses 100001 to
 * render above the task editor (NOT MUI's default 1300, nor the plan's 1400).
 *
 * Props:
 *   open         {boolean}  controls visibility
 *   onClose      {function} called on Close / backdrop click
 *   packageId    {string}   used for all history API calls
 *   autoDeskName {string}   ACC / Autodesk name — header subtitle
 *   packageName  {string}   package description — header subtitle
 *   area         {string}   area — header line below subtitle
 *   defaultTab   {string}   which tab opens first (default 'status')
 */

// Single source of truth for the switcher pills and panel rendering.
const HISTORY_TABS = [
  { key: 'status', label: 'Status', title: 'Status Changes' },
  { key: 'timecards', label: 'Timecards', title: 'Timecard Detail' },
  { key: 'earned', label: 'Earned hrs', title: 'Earned Hours History' },
  { key: 'budget-split', label: 'Budget split', title: 'Budget Split History' },
  {
    key: 'budget-change',
    label: 'Budget changes',
    title: 'Budget Change History',
  },
  { key: 'snapshots', label: 'Snapshots', title: 'Weekly Snapshots' },
];

// tabKey -> WorkflowAPI fetcher (each returns the unwrapped { ... } payload).
const FETCHERS = {
  status: WorkflowAPI.getPackageStatusHistory,
  timecards: WorkflowAPI.getPackageTimecardHistory,
  earned: WorkflowAPI.getPackageEarnedHistory,
  'budget-split': WorkflowAPI.getPackageBudgetSplitHistory,
  'budget-change': WorkflowAPI.getPackageBudgetChangeHistory,
  snapshots: WorkflowAPI.getPackageSnapshotsHistory,
};

function PackageHistoryModal({
  open,
  onClose,
  packageId,
  autoDeskName,
  packageName,
  area,
  defaultTab = 'status',
}) {
  const auth = useContext(AuthContext);
  const token = auth?.token;

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [loadedData, setLoadedData] = useState({}); // { [tabKey]: payload }
  const [loading, setLoading] = useState({}); // { [tabKey]: boolean }
  const [errors, setErrors] = useState({}); // { [tabKey]: message | null }

  // Lazy-load a tab's data once per modal session. `undefined` in loadedData is
  // the "not yet fetched" sentinel — an empty result caches as { ...: [] } and
  // is never refetched.
  const fetchTabData = async (tabKey) => {
    if (!packageId) return;
    if (loadedData[tabKey] !== undefined || loading[tabKey]) return;

    setLoading((prev) => ({ ...prev, [tabKey]: true }));
    setErrors((prev) => ({ ...prev, [tabKey]: null }));
    try {
      const data = await FETCHERS[tabKey](packageId, token);
      setLoadedData((prev) => ({ ...prev, [tabKey]: data }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [tabKey]: 'Failed to load history.' }));
    } finally {
      setLoading((prev) => ({ ...prev, [tabKey]: false }));
    }
  };

  // Lifecycle: sync the active tab to defaultTab on open; clear all cache on
  // close so reopening (possibly for a different package) starts fresh.
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
    } else {
      setActiveTab(defaultTab);
      setLoadedData({});
      setLoading({});
      setErrors({});
    }
  }, [open, defaultTab]);

  // Fetch the active tab's data on open and whenever the user switches tabs.
  useEffect(() => {
    if (!open || !packageId) return;
    fetchTabData(activeTab);
    // fetchTabData intentionally excluded — it is recreated every render and
    // self-guards against duplicate/refetch via the loadedData/loading checks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, packageId, activeTab]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
  };

  const activeTabConfig =
    HISTORY_TABS.find((t) => t.key === activeTab) || HISTORY_TABS[0];

  const renderActivePanel = () => {
    const isLoading = loading[activeTab];
    const error = errors[activeTab];
    const data = loadedData[activeTab];

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      );
    }
    if (error) {
      return (
        <Typography
          variant="body2"
          sx={{ textAlign: 'center', py: 4, color: 'var(--phm-danger)' }}
        >
          {error}
        </Typography>
      );
    }
    if (data === undefined) return null;

    switch (activeTab) {
      case 'status':
        return <StatusHistoryTable rows={data.history} />;
      case 'timecards':
        return <TimecardDetailTable rows={data.timecards} />;
      case 'earned':
        return <EarnedHistoryTable rows={data.history} />;
      case 'budget-split':
        return <BudgetSplitTable rows={data.history} />;
      case 'budget-change':
        return <BudgetChangeTable rows={data.history} />;
      case 'snapshots':
        return <WeeklySnapshotsTable rows={data.snapshots} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      className="package-history-modal"
      PaperProps={{
        sx: {
          width: activeTab === 'budget-change' ? 1200 : 900,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: '80vh',
        },
      }}
      sx={{ zIndex: 100001 }}
    >
      {/* Header: active history title + package identity */}
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
          {activeTabConfig.title}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          noWrap
          title={`${autoDeskName || ''}${
            autoDeskName && packageName ? ' — ' : ''
          }${packageName || ''}`}
        >
          {autoDeskName}
          {autoDeskName && packageName ? ' — ' : ''}
          {packageName}
        </Typography>
        {area ? (
          <Typography variant="caption" color="text.secondary">
            Area: {area}
          </Typography>
        ) : null}
      </DialogTitle>

      {/* Switcher row: one pill per history type */}
      <Box
        className="package-history-switcher"
        sx={{
          px: 2.5,
          py: 1,
          borderTop: 1,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.default',
          display: 'flex',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        {HISTORY_TABS.map((tab) => (
          <Chip
            key={tab.key}
            label={tab.label}
            onClick={() => handleTabChange(tab.key)}
            variant={activeTab === tab.key ? 'filled' : 'outlined'}
            color={activeTab === tab.key ? 'primary' : 'default'}
            size="small"
            sx={{ flexShrink: 0 }}
          />
        ))}
      </Box>

      {/* Content: active tab's panel */}
      <DialogContent
        sx={{
          p: 0,
          overflowY: 'auto',
          overflowX: activeTab === 'budget-change' ? 'hidden' : 'auto',
        }}
      >
        <Box sx={{ px: 2.5, py: 1.5 }}>{renderActivePanel()}</Box>
      </DialogContent>

      {/* Footer */}
      <DialogActions>
        <Button onClick={onClose} variant="outlined" size="small">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default PackageHistoryModal;
